import { Link, useLocation } from "react-router-dom";
import { PlanMiniChip } from "@/components/plan/PlanWidgets";
import { LayoutGrid, Settings2, CalendarDays, Building2, Check, FileSpreadsheet, Wallet, Utensils, LogOut, Shield, Home, BookOpenCheck } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useActiveSchool } from "@/lib/activeSchool";
import { useAuth } from "@/lib/AuthContext";
// iter135 — legacy mobile chrome retired; AuthedShell now hosts AppHeader +
// BottomNav + SlideOutDrawer + FAB globally. Import removed to fix the
// stacked-header double on mobile.
import InboxBell from "@/components/InboxBell";
import AdminSchoolSearch from "@/components/AdminSchoolSearch";

const items = [
  { to: "/dashboard", label: "Home", icon: Home, testId: "nav-dashboard" },
  { to: "/", label: "Quarterly Reports", icon: LayoutGrid, testId: "nav-generator" },
  { to: "/daily", label: "Daily Entry", icon: CalendarDays, testId: "nav-daily" },
  { to: "/funds", label: "Fund Management", icon: Wallet, testId: "nav-funds" },
  { to: "/consumption", label: "Daily Consumption", icon: Utensils, testId: "nav-consumption" },
  { to: "/mdcf", label: "Monthly MDCF", icon: FileSpreadsheet, testId: "nav-mdcf" },
  { to: "/reports", label: "Reports", icon: BookOpenCheck, testId: "nav-reports" },
  { to: "/setup", label: "Setup", icon: Settings2, testId: "nav-setup" },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const { profiles, activeId, setActiveId, active } = useActiveSchool();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  // For unverified school users, hide every feature nav item — they only
  // see "Home" until the admin verifies them. Admin and verified school
  // users see the full nav.
  const visibleItems = (user?.role === "school" && !user?.verified)
    ? items.filter((i) => i.to === "/dashboard")
    : items;

  return (
    <>
    <nav className="sticky top-0 z-30 hidden border-b border-border bg-sand-50/95 backdrop-blur supports-[backdrop-filter]:bg-sand-50/80 md:block no-print">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-2 sm:px-6 lg:px-8">
        <Link to={isAdmin ? "/admin" : "/dashboard"} className="mr-2 flex items-center gap-2 text-ink-900" data-testid="nav-home">
          <img src="/logo192.png" alt="MDM Sahayak" className="h-8 w-8 rounded-lg" />
          <span className="font-heading text-base font-semibold">MDM Sahayak</span>
          {isAdmin && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 flex items-center gap-1"><Shield className="h-2.5 w-2.5" /> ADMIN</span>}
        </Link>

        <div className="flex items-center gap-1 overflow-x-auto">
          {isAdmin && (
            <Link to="/admin" data-testid="nav-admin"
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${pathname === "/admin" ? "bg-clay text-white" : "text-ink-700 hover:bg-sand-100"}`}>
              <Shield className="h-3.5 w-3.5" /> <span>Admin</span>
            </Link>
          )}
          {items.map(({ to, label, icon: Icon, testId }) => {
            // Skip items not in the visible list (unverified school users see only Home).
            if (!visibleItems.includes(items.find((i) => i.to === to))) return null;
            const active2 = (to === "/" && pathname === "/") || (to !== "/" && pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm font-medium transition-all duration-200 ${
                  active2 ? "bg-clay text-white" : "text-ink-700 hover:bg-sand-100 hover:text-ink-900"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{label}</span>
              </Link>
            );
          })}

          {/* Inbox bell with unread badge — visible for both admin and school users
              (even unverified schools so they can read admin onboarding messages). */}
          {user && (
            <InboxBell role={user.role} isActive={pathname === "/inbox" || (user.role === "admin" && pathname === "/admin")} />
          )}
        </div>

        {/* Active School badge — admin can switch any school; school user is locked to their own. */}
        <div className="ml-auto flex items-center gap-2">
          <div className="flex min-w-0 max-w-[220px] items-center gap-2" data-testid="active-school-badge">
            <Building2 className="h-3.5 w-3.5 text-moss-dark shrink-0" />
            {profiles.length === 0 ? (
              <span className="text-xs text-ink-700">No school yet</span>
            ) : isAdmin ? (
              <Select value={activeId || ""} onValueChange={setActiveId}>
                <SelectTrigger data-testid="active-school-select" className="h-8 bg-white border-border text-xs">
                  <SelectValue placeholder="Pick active school">
                    {active ? (
                      <span className="flex items-center gap-1.5">
                        <Check className="h-3 w-3 text-moss-dark" />
                        <span className="truncate font-medium">{active.schoolName}</span>
                      </span>
                    ) : "Pick active school"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`active-school-option-${p.udiseCode || p.id}`}>
                      <span className="font-medium">{p.schoolName}</span>
                      <span className="ml-2 text-xs text-ink-700">{p.udiseCode}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="truncate text-xs font-medium" title={active?.schoolName}>
                <Check className="mr-1 inline h-3 w-3 text-moss-dark" />
                {active?.schoolName || user?.schoolName || "—"}
              </span>
            )}
          </div>
            {user?.role === "school" && (
              <Link to="/my-plan" data-testid="nav-my-plan" className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors duration-200 ${pathname === "/my-plan" ? "bg-clay text-white" : "text-ink-700 hover:bg-sand-100 hover:text-ink-900"}`}>My Plan</Link>
            )}
            {user?.role === "school" && <PlanMiniChip testId="nav-plan-chip" />}
          {user && (
            <>
              {isAdmin && <AdminSchoolSearch />}
              <Button size="sm" variant="ghost" onClick={logout} className="text-ink-700 hover:text-red-600" data-testid="nav-logout-btn" title="Sign out">
                <LogOut className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
    </>
  );
}
